<template>
  <div class="rich-text-editor rounded-xl border border-slate-200 bg-white overflow-hidden">
    <!-- 工具栏 -->
    <div v-if="editor" class="toolbar flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
      <!-- 标题 -->
      <select
        class="toolbar-select"
        :value="currentHeading"
        @change="setHeading($event.target.value)"
      >
        <option value="0">正文</option>
        <option value="1">标题 1</option>
        <option value="2">标题 2</option>
        <option value="3">标题 3</option>
        <option value="4">标题 4</option>
        <option value="5">标题 5</option>
        <option value="6">标题 6</option>
      </select>

      <!-- 字体 -->
      <select
        class="toolbar-select"
        :value="currentFontFamily"
        @change="setFontFamily($event.target.value)"
      >
        <option value="">默认字体</option>
        <option value="Arial">Arial</option>
        <option value="Times New Roman">Times New Roman</option>
        <option value="Georgia">Georgia</option>
        <option value="Courier New">Courier New</option>
        <option value="Verdana">Verdana</option>
        <option value="Microsoft YaHei">微软雅黑</option>
        <option value="SimSun">宋体</option>
        <option value="SimHei">黑体</option>
        <option value="KaiTi">楷体</option>
      </select>

      <!-- 字号 -->
      <select
        class="toolbar-select"
        :value="currentFontSize"
        @change="setFontSize($event.target.value)"
      >
        <option value="">默认</option>
        <option value="12px">12px</option>
        <option value="14px">14px</option>
        <option value="16px">16px</option>
        <option value="18px">18px</option>
        <option value="20px">20px</option>
        <option value="24px">24px</option>
        <option value="28px">28px</option>
        <option value="32px">32px</option>
        <option value="36px">36px</option>
      </select>

      <div class="toolbar-divider" />

      <!-- 加粗 / 斜体 / 下划线 / 删除线 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()"
        title="加粗"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"
        title="斜体"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()"
        title="下划线"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" /><line x1="4" y1="21" x2="20" y2="21" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('strike') }"
        @click="editor.chain().focus().toggleStrike().run()"
        title="删除线"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="4" y1="12" x2="20" y2="12" /><path d="M17.5 7.5c0-2-1.5-3.5-5.5-3.5S6 5 6 7.5c0 4 12 3 12 8 0 2.5-2.5 4.5-6 4.5s-6-2-6-4.5" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 文字颜色 -->
      <div class="relative">
        <button class="toolbar-btn" title="文字颜色">
          <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9.9 4.24A.5.5 0 0 1 10.5 4h3a.5.5 0 0 1 .47.33L18 16h-2l-1.3-4H9.3L8 16H6l3.9-11.76z" fill="currentColor" stroke="none" />
          </svg>
          <div class="absolute bottom-0.5 left-1 right-1 h-1 rounded-sm" :style="{ backgroundColor: currentColor || '#000000' }" />
        </button>
        <input
          type="color"
          class="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          :value="currentColor || '#000000'"
          @input="editor.chain().focus().setColor($event.target.value).run()"
          title="文字颜色"
        >
      </div>

      <!-- 背景颜色 -->
      <div class="relative">
        <button class="toolbar-btn" title="背景颜色">
          <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 3l6 9H6l6-9z" fill="currentColor" stroke="none" opacity="0.3" />
            <rect x="3" y="15" width="18" height="5" rx="1" fill="currentColor" stroke="none" opacity="0.5" />
          </svg>
          <div class="absolute bottom-0.5 left-1 right-1 h-1 rounded-sm" :style="{ backgroundColor: currentHighlight || '#ffff00' }" />
        </button>
        <input
          type="color"
          class="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          :value="currentHighlight || '#ffff00'"
          @input="editor.chain().focus().toggleHighlight({ color: $event.target.value }).run()"
          title="背景颜色"
        >
      </div>

      <div class="toolbar-divider" />

      <!-- 上标 / 下标 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('superscript') }"
        @click="editor.chain().focus().toggleSuperscript().run()"
        title="上标"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor">
          <text x="2" y="18" font-size="14" font-weight="bold">X</text>
          <text x="14" y="10" font-size="8" font-weight="bold">2</text>
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('subscript') }"
        @click="editor.chain().focus().toggleSubscript().run()"
        title="下标"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor">
          <text x="2" y="16" font-size="14" font-weight="bold">X</text>
          <text x="14" y="20" font-size="8" font-weight="bold">2</text>
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 缩进 -->
      <button
        class="toolbar-btn"
        @click="handleOutdent"
        title="减少缩进"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="4" x2="21" y2="4" /><line x1="11" y1="9" x2="21" y2="9" /><line x1="11" y1="14" x2="21" y2="14" /><line x1="3" y1="19" x2="21" y2="19" />
          <polyline points="7 14 3 11.5 7 9" />
        </svg>
      </button>
      <button
        class="toolbar-btn"
        @click="handleIndent"
        title="增加缩进"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="4" x2="21" y2="4" /><line x1="11" y1="9" x2="21" y2="9" /><line x1="11" y1="14" x2="21" y2="14" /><line x1="3" y1="19" x2="21" y2="19" />
          <polyline points="3 14 7 11.5 3 9" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 对齐 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'left' }) }"
        @click="editor.chain().focus().setTextAlign('left').run()"
        title="左对齐"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'center' }) }"
        @click="editor.chain().focus().setTextAlign('center').run()"
        title="居中"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'right' }) }"
        @click="editor.chain().focus().setTextAlign('right').run()"
        title="右对齐"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="6" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'justify' }) }"
        @click="editor.chain().focus().setTextAlign('justify').run()"
        title="两端对齐"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 有序列表 / 无序列表 / 任务列表 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
        title="有序列表"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
          <text x="2" y="8" font-size="7" fill="currentColor" stroke="none" font-weight="bold">1</text>
          <text x="2" y="14" font-size="7" fill="currentColor" stroke="none" font-weight="bold">2</text>
          <text x="2" y="20" font-size="7" fill="currentColor" stroke="none" font-weight="bold">3</text>
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
        title="无序列表"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" />
          <circle cx="4.5" cy="6" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('taskList') }"
        @click="editor.chain().focus().toggleTaskList().run()"
        title="任务列表"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="5" width="6" height="6" rx="1" /><polyline points="5 8 6 9 9 6" stroke-width="1.5" /><line x1="13" y1="8" x2="21" y2="8" />
          <rect x="3" y="14" width="6" height="6" rx="1" /><line x1="13" y1="17" x2="21" y2="17" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 引用 / 代码块 / 分隔线 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('blockquote') }"
        @click="editor.chain().focus().toggleBlockquote().run()"
        title="引用"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor">
          <path d="M10 8.5c0 1.93-1.57 3.5-3.5 3.5H6v1c0 1.66 1.34 3 3 3v2c-2.76 0-5-2.24-5-5V7h4.5C9.33 7 10 7.67 10 8.5zm8 0c0 1.93-1.57 3.5-3.5 3.5H14v1c0 1.66 1.34 3 3 3v2c-2.76 0-5-2.24-5-5V7h4.5c.83 0 1.5.67 1.5 1.5z" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('codeBlock') }"
        @click="editor.chain().focus().toggleCodeBlock().run()"
        title="代码块"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
        </svg>
      </button>
      <button
        class="toolbar-btn"
        @click="editor.chain().focus().setHorizontalRule().run()"
        title="分隔线"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="3" y1="12" x2="21" y2="12" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 表格 -->
      <button
        class="toolbar-btn"
        @click="insertTable"
        title="插入表格"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
        </svg>
      </button>

      <!-- 表格操作按钮（仅在光标在表格内时显示） -->
      <template v-if="editor.isActive('table')">
        <button class="toolbar-btn" @click="editor.chain().focus().addColumnAfter().run()" title="右侧添加列">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="12" height="18" rx="1" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="19" y1="8" x2="19" y2="16" /><line x1="15" y1="12" x2="23" y2="12" />
          </svg>
        </button>
        <button class="toolbar-btn" @click="editor.chain().focus().addRowAfter().run()" title="下方添加行">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="12" rx="1" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="8" y1="19" x2="16" y2="19" /><line x1="12" y1="15" x2="12" y2="23" />
          </svg>
        </button>
        <button class="toolbar-btn" @click="editor.chain().focus().mergeCells().run()" title="合并单元格">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="12" y1="3" x2="12" y2="12" /><polyline points="8 7 12 5 16 7" stroke-width="1.5" />
          </svg>
        </button>
        <button class="toolbar-btn" @click="editor.chain().focus().splitCell().run()" title="拆分单元格">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="12" y1="3" x2="12" y2="21" /><polyline points="8 10 12 12 16 10" stroke-width="1.5" />
          </svg>
        </button>
        <button class="toolbar-btn text-red-500 hover:text-red-600" @click="editor.chain().focus().deleteColumn().run()" title="删除列">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="6" y="3" width="12" height="18" rx="1" /><line x1="6" y1="9" x2="18" y2="9" /><line x1="6" y1="15" x2="18" y2="15" /><line x1="2" y1="12" x2="4" y2="12" /><line x1="20" y1="12" x2="22" y2="12" />
          </svg>
        </button>
        <button class="toolbar-btn text-red-500 hover:text-red-600" @click="editor.chain().focus().deleteRow().run()" title="删除行">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="6" width="18" height="12" rx="1" /><line x1="9" y1="6" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="18" /><line x1="12" y1="2" x2="12" y2="4" /><line x1="12" y1="20" x2="12" y2="22" />
          </svg>
        </button>
        <button class="toolbar-btn text-red-500 hover:text-red-600" @click="editor.chain().focus().deleteTable().run()" title="删除表格">
          <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" />
          </svg>
        </button>
      </template>

      <div class="toolbar-divider" />

      <!-- 链接 / 图片 / 视频 -->
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('link') }"
        @click="toggleLink"
        title="链接"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </button>
      <button
        class="toolbar-btn"
        @click="triggerImageUpload"
        title="图片"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" />
        </svg>
      </button>
      <button
        class="toolbar-btn"
        @click="triggerVideoUpload"
        title="视频"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="4" width="20" height="16" rx="2" /><polygon points="10 8 16 12 10 16" fill="currentColor" stroke="none" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 撤销 / 重做 -->
      <button
        class="toolbar-btn"
        :disabled="!editor.can().undo()"
        @click="editor.chain().focus().undo().run()"
        title="撤销"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      </button>
      <button
        class="toolbar-btn"
        :disabled="!editor.can().redo()"
        @click="editor.chain().focus().redo().run()"
        title="重做"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
      </button>

      <div class="toolbar-divider" />

      <!-- 清除格式 -->
      <button
        class="toolbar-btn"
        @click="editor.chain().focus().clearNodes().unsetAllMarks().run()"
        title="清除格式"
      >
        <svg viewBox="0 0 24 24" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M4 7h16" /><path d="M10 4l-2 16" /><path d="M17 17l4 4" /><path d="M21 17l-4 4" />
        </svg>
      </button>
    </div>

    <!-- 气泡菜单（选中文字时浮现） -->
    <bubble-menu
      v-if="editor"
      :editor="editor"
      :tippy-options="{ duration: 150 }"
      class="bubble-menu flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white px-1 py-1 shadow-lg"
    >
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()"
        title="加粗"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" /><path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"
        title="斜体"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="19" y1="4" x2="10" y2="4" /><line x1="14" y1="20" x2="5" y2="20" /><line x1="15" y1="4" x2="9" y2="20" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()"
        title="下划线"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" /><line x1="4" y1="21" x2="20" y2="21" />
        </svg>
      </button>
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('strike') }"
        @click="editor.chain().focus().toggleStrike().run()"
        title="删除线"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="4" y1="12" x2="20" y2="12" /><path d="M17.5 7.5c0-2-1.5-3.5-5.5-3.5S6 5 6 7.5c0 4 12 3 12 8 0 2.5-2.5 4.5-6 4.5s-6-2-6-4.5" />
        </svg>
      </button>
      <div class="mx-0.5 h-4 w-px bg-slate-200" />
      <button
        class="toolbar-btn" :class="{ active: editor.isActive('link') }"
        @click="toggleLink"
        title="链接"
      >
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </button>
    </bubble-menu>

    <!-- 编辑器内容区 -->
    <editor-content :editor="editor" />

    <!-- 底部状态栏：字符统计 -->
    <div v-if="editor" class="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-1 text-xs text-slate-400">
      <span>{{ characterCount }} 字符</span>
      <span>{{ wordCount }} 字</span>
    </div>

    <!-- 隐藏的文件上传 -->
    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      class="hidden"
      @change="handleImageUpload"
    >
    <input
      ref="videoInput"
      type="file"
      accept="video/*"
      class="hidden"
      @change="handleVideoUpload"
    >
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { BubbleMenu } from '@tiptap/vue-3/menus'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import FontFamily from '@tiptap/extension-font-family'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import CharacterCount from '@tiptap/extension-character-count'
import Typography from '@tiptap/extension-typography'
import { common, createLowlight } from 'lowlight'
import { Extension, Node, mergeAttributes } from '@tiptap/core'
import { ElMessage } from 'element-plus'
import { uploadFile } from '@/api/file'
import { resolveUploadedFileUrl } from '@/utils/uploadUrl'

const lowlight = createLowlight(common)

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '请输入内容...'
  },
  minHeight: {
    type: String,
    default: '200px'
  },
  uploadModule: {
    type: String,
    default: 'editor'
  }
})

const emit = defineEmits(['update:modelValue'])

const imageInput = ref(null)
const videoInput = ref(null)

// 自定义 FontSize 扩展
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] }
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: el => el.style.fontSize?.replace(/['"]+/g, '') || null,
          renderHTML: attrs => {
            if (!attrs.fontSize) return {}
            return { style: `font-size: ${attrs.fontSize}` }
          }
        }
      }
    }]
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run()
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run()
      }
    }
  }
})

const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {
        controls: 'controls'
      }
    }
  },

  addAttributes() {
    return {
      src: {
        default: null
      }
    }
  },

  parseHTML() {
    return [{ tag: 'video[src]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
  },

  addCommands() {
    return {
      setVideo: (attrs) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs
        })
      }
    }
  }
})

const editor = useEditor({
  content: '',
  extensions: [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      codeBlock: false // 使用 CodeBlockLowlight 替代
    }),
    Underline,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right', 'justify']
    }),
    Subscript,
    Superscript,
    Link.configure({
      openOnClick: false
    }),
    Image.configure({
      inline: false
    }),
    Video,
    FontFamily,
    FontSize,
    Placeholder.configure({
      placeholder: props.placeholder
    }),
    Table.configure({
      resizable: true
    }),
    TableRow,
    TableCell,
    TableHeader,
    TaskList,
    TaskItem.configure({
      nested: true
    }),
    CodeBlockLowlight.configure({
      lowlight
    }),
    CharacterCount,
    Typography
  ],
  onUpdate: ({ editor: e }) => {
    const html = e.getHTML()
    emit('update:modelValue', html === '<p></p>' ? '' : html)
  }
})

// 字符统计
const characterCount = computed(() => {
  if (!editor.value) return 0
  return editor.value.storage.characterCount.characters()
})

const wordCount = computed(() => {
  if (!editor.value) return 0
  return editor.value.storage.characterCount.words()
})

// 外部值同步
watch(() => props.modelValue, (val) => {
  if (!editor.value) return
  const current = editor.value.getHTML()
  const normalizedVal = val || ''
  const normalizedCurrent = current === '<p></p>' ? '' : current
  if (normalizedVal !== normalizedCurrent) {
    editor.value.commands.setContent(val || '', false)
  }
}, { flush: 'post' })

// 当前状态（用于工具栏高亮）
const currentHeading = computed(() => {
  if (!editor.value) return '0'
  for (let i = 1; i <= 6; i++) {
    if (editor.value.isActive('heading', { level: i })) return String(i)
  }
  return '0'
})

const currentFontFamily = computed(() => {
  if (!editor.value) return ''
  return editor.value.getAttributes('textStyle').fontFamily || ''
})

const currentFontSize = computed(() => {
  if (!editor.value) return ''
  return editor.value.getAttributes('textStyle').fontSize || ''
})

const currentColor = computed(() => {
  if (!editor.value) return ''
  return editor.value.getAttributes('textStyle').color || ''
})

const currentHighlight = computed(() => {
  if (!editor.value) return ''
  return editor.value.getAttributes('highlight').color || ''
})

// 工具栏操作
const setHeading = (level) => {
  if (!editor.value) return
  const lvl = parseInt(level)
  if (lvl === 0) {
    editor.value.chain().focus().setParagraph().run()
  } else {
    editor.value.chain().focus().toggleHeading({ level: lvl }).run()
  }
}

const setFontFamily = (font) => {
  if (!editor.value) return
  if (!font) {
    editor.value.chain().focus().unsetFontFamily().run()
  } else {
    editor.value.chain().focus().setFontFamily(font).run()
  }
}

const setFontSize = (size) => {
  if (!editor.value) return
  if (!size) {
    editor.value.chain().focus().unsetFontSize().run()
  } else {
    editor.value.chain().focus().setFontSize(size).run()
  }
}

const handleIndent = () => {
  if (!editor.value) return
  if (editor.value.isActive('listItem')) {
    editor.value.chain().focus().sinkListItem('listItem').run()
  }
}

const handleOutdent = () => {
  if (!editor.value) return
  if (editor.value.isActive('listItem')) {
    editor.value.chain().focus().liftListItem('listItem').run()
  }
}

const toggleLink = () => {
  if (!editor.value) return
  if (editor.value.isActive('link')) {
    editor.value.chain().focus().unsetLink().run()
    return
  }
  const url = window.prompt('请输入链接地址', 'https://')
  if (url) {
    editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}

const triggerImageUpload = () => {
  imageInput.value?.click()
}

const triggerVideoUpload = () => {
  videoInput.value?.click()
}

const handleImageUpload = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    ElMessage.warning('请选择图片文件')
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过10MB')
    return
  }

  try {
    const res = await uploadFile(file, props.uploadModule, 'public')
    const imageUrl = resolveUploadedFileUrl(res, process.env.VUE_APP_API_BASE_URL || '')
    if (!imageUrl) {
      ElMessage.error('上传失败，未获取到图片地址')
      return
    }
    editor.value.chain().focus().setImage({ src: imageUrl }).run()
  } catch (error) {
    ElMessage.error(error?.message || '图片上传失败')
  } finally {
    event.target.value = ''
  }
}

const handleVideoUpload = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  if (!file.type.startsWith('video/')) {
    ElMessage.warning('请选择视频文件')
    return
  }
  if (file.size > 100 * 1024 * 1024) {
    ElMessage.warning('视频大小不能超过100MB')
    return
  }

  try {
    const res = await uploadFile(file, props.uploadModule, 'public')
    const videoUrl = resolveUploadedFileUrl(res, process.env.VUE_APP_API_BASE_URL || '')
    if (!videoUrl) {
      ElMessage.error('上传失败，未获取到视频地址')
      return
    }
    editor.value.chain().focus().setVideo({ src: videoUrl }).run()
  } catch (error) {
    ElMessage.error(error?.message || '视频上传失败')
  } finally {
    event.target.value = ''
  }
}

const insertTable = () => {
  if (!editor.value) return
  editor.value.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
}

// 组件挂载后，等待编辑器 ready 再同步一次内容（处理 initForm 异步返回的情况）
onMounted(async () => {
  if (!editor.value) return
  await editor.value.ready
  const val = props.modelValue || ''
  const current = editor.value.getHTML()
  const normalizedVal = val
  const normalizedCurrent = current === '<p></p>' ? '' : current
  if (normalizedVal !== normalizedCurrent) {
    editor.value.commands.setContent(val, false)
  }
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})
</script>

<style scoped>
.toolbar-select {
  @apply h-7 rounded border border-slate-200 bg-white px-1.5 text-xs text-slate-600 outline-none transition-colors hover:border-slate-300 focus:border-primary-500;
  background-image: none;
  padding-right: 6px !important;
}

.toolbar-btn {
  @apply flex h-7 w-7 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed;
}

.toolbar-btn.active {
  @apply bg-primary-50 text-primary-600;
}

.toolbar-divider {
  @apply mx-1 h-5 w-px bg-slate-200;
}

:deep(.ProseMirror) {
  padding: 12px 16px;
  min-height: v-bind(minHeight);
  outline: none;
  font-size: 14px;
  color: #0f172a;
  line-height: 1.6;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #94a3b8;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

:deep(.ProseMirror h1) { font-size: 2em; font-weight: 700; margin: 0.67em 0; }
:deep(.ProseMirror h2) { font-size: 1.5em; font-weight: 700; margin: 0.75em 0; }
:deep(.ProseMirror h3) { font-size: 1.25em; font-weight: 600; margin: 0.83em 0; }
:deep(.ProseMirror h4) { font-size: 1.1em; font-weight: 600; margin: 0.9em 0; }
:deep(.ProseMirror h5) { font-size: 1em; font-weight: 600; margin: 1em 0; }
:deep(.ProseMirror h6) { font-size: 0.9em; font-weight: 600; margin: 1em 0; }

:deep(.ProseMirror p) { margin: 0.5em 0; }

:deep(.ProseMirror ul) { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
:deep(.ProseMirror ol) { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
:deep(.ProseMirror li) { margin: 0.25em 0; }
:deep(.ProseMirror li p) { margin: 0; }

/* 任务列表样式 */
:deep(.ProseMirror ul[data-type="taskList"]) {
  list-style: none;
  padding-left: 0;
  margin: 0.5em 0;
}

:deep(.ProseMirror ul[data-type="taskList"] li) {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 4px 0;
}

:deep(.ProseMirror ul[data-type="taskList"] li > label) {
  flex-shrink: 0;
  margin-top: 3px;
  user-select: none;
}

:deep(.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]) {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2563eb;
}

:deep(.ProseMirror ul[data-type="taskList"] li > div) {
  flex: 1;
}

:deep(.ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div) {
  text-decoration: line-through;
  color: #94a3b8;
}

:deep(.ProseMirror blockquote) {
  border-left: 4px solid #2563eb;
  padding-left: 1em;
  margin: 0.75em 0;
  color: #64748b;
}

/* 代码块高亮样式 */
:deep(.ProseMirror pre) {
  background: #1e293b;
  color: #e2e8f0;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  margin: 0.75em 0;
}

:deep(.ProseMirror pre code) {
  color: inherit;
  background: none;
  font-size: inherit;
  padding: 0;
}

/* lowlight 语法高亮 token 颜色 */
:deep(.ProseMirror pre .hljs-comment),
:deep(.ProseMirror pre .hljs-quote) { color: #616e7c; font-style: italic; }
:deep(.ProseMirror pre .hljs-keyword),
:deep(.ProseMirror pre .hljs-selector-tag),
:deep(.ProseMirror pre .hljs-addition) { color: #c792ea; }
:deep(.ProseMirror pre .hljs-number),
:deep(.ProseMirror pre .hljs-string),
:deep(.ProseMirror pre .hljs-meta .hljs-meta-string),
:deep(.ProseMirror pre .hljs-literal),
:deep(.ProseMirror pre .hljs-doctag),
:deep(.ProseMirror pre .hljs-regexp) { color: #f78c6c; }
:deep(.ProseMirror pre .hljs-title),
:deep(.ProseMirror pre .hljs-section),
:deep(.ProseMirror pre .hljs-name),
:deep(.ProseMirror pre .hljs-selector-id),
:deep(.ProseMirror pre .hljs-selector-class) { color: #82aaff; }
:deep(.ProseMirror pre .hljs-attribute),
:deep(.ProseMirror pre .hljs-attr),
:deep(.ProseMirror pre .hljs-variable),
:deep(.ProseMirror pre .hljs-template-variable),
:deep(.ProseMirror pre .hljs-class .hljs-title),
:deep(.ProseMirror pre .hljs-type) { color: #ffcb6b; }
:deep(.ProseMirror pre .hljs-symbol),
:deep(.ProseMirror pre .hljs-bullet),
:deep(.ProseMirror pre .hljs-subst),
:deep(.ProseMirror pre .hljs-meta),
:deep(.ProseMirror pre .hljs-link) { color: #89ddff; }
:deep(.ProseMirror pre .hljs-built_in),
:deep(.ProseMirror pre .hljs-deletion) { color: #ff5370; }

:deep(.ProseMirror code) {
  background: #f1f5f9;
  color: #e11d48;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
}

:deep(.ProseMirror a) {
  color: #2563eb;
  text-decoration: underline;
  cursor: pointer;
}

:deep(.ProseMirror img) {
  display: block;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 8px auto;
}

:deep(.ProseMirror video) {
  display: block;
  width: 100%;
  max-width: 640px;
  height: auto;
  border-radius: 8px;
  margin: 8px auto;
}

:deep(.ProseMirror hr) {
  border: none;
  border-top: 2px solid #e2e8f0;
  margin: 1em 0;
}

:deep(.ProseMirror mark) {
  border-radius: 2px;
  padding: 1px 2px;
}

/* 表格样式 */
:deep(.ProseMirror table) {
  border-collapse: collapse;
  width: 100%;
  margin: 0.75em 0;
  overflow: hidden;
  table-layout: fixed;
}

:deep(.ProseMirror table td),
:deep(.ProseMirror table th) {
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  vertical-align: top;
  min-width: 80px;
  position: relative;
}

:deep(.ProseMirror table th) {
  background: #f8fafc;
  font-weight: 600;
  text-align: left;
}

:deep(.ProseMirror table .selectedCell) {
  background: #dbeafe;
}

:deep(.ProseMirror .tableWrapper) {
  overflow-x: auto;
  margin: 0.75em 0;
}

/* iframe */
:deep(.ProseMirror iframe) {
  width: 100%;
  max-width: 640px;
  aspect-ratio: 16 / 9;
  border: none;
  border-radius: 8px;
  margin: 8px auto;
}
</style>
